import React, { useState, useEffect } from 'react';
import vendorDashboardService from '../../services/vendorDashboardService';
import ExportButton from '../../components/ExportButton/ExportButton';
import { exportVendorDashboardTasks } from '../../services/exportService';
import { toast } from 'react-toastify';
import './VendorTasks.css';

const VendorTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    search: '',
    page: 1,
    limit: 12
  });
  const [pagination, setPagination] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [analytics, setAnalytics] = useState({
    total: 0,
    byStatus: {},
    byPriority: {},
    overdue: 0,
    dueToday: 0,
    dueThisWeek: 0,
    completionRate: 0
  });

  useEffect(() => {
    loadTasks();
  }, [filters]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const data = await vendorDashboardService.getTasks(filters);
      setTasks(data.tasks || []);
      setPagination(data.pagination);
      calculateAnalytics(data.tasks || []);
      setError(null);
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (taskList) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekFromNow = new Date(today);
    weekFromNow.setDate(weekFromNow.getDate() + 7);

    const stats = {
      total: taskList.length,
      byStatus: {},
      byPriority: {},
      overdue: 0,
      dueToday: 0,
      dueThisWeek: 0,
      completionRate: 0
    };

    taskList.forEach(task => {
      // Count by status
      stats.byStatus[task.status] = (stats.byStatus[task.status] || 0) + 1;
      
      // Count by priority
      stats.byPriority[task.priority] = (stats.byPriority[task.priority] || 0) + 1;
      
      // Check due dates
      if (task.due_date) {
        const dueDate = new Date(task.due_date);
        const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
        
        if (dueDateOnly < today && task.status !== 'completed') {
          stats.overdue++;
        } else if (dueDateOnly.getTime() === today.getTime()) {
          stats.dueToday++;
        } else if (dueDateOnly > today && dueDateOnly <= weekFromNow) {
          stats.dueThisWeek++;
        }
      }
    });

    // Calculate completion rate
    const completed = stats.byStatus['completed'] || 0;
    stats.completionRate = stats.total > 0 ? Math.round((completed / stats.total) * 100) : 0;

    setAnalytics(stats);
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await vendorDashboardService.updateTaskStatus(taskId, newStatus);
      loadTasks();
    } catch (err) {
      console.error('Error updating task status:', err);
      alert('Failed to update task status');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: '#dc2626',
      high: '#f59e0b',
      medium: '#3b82f6',
      low: '#10b981'
    };
    return colors[priority] || '#6b7280';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      in_progress: '#3b82f6',
      completed: '#10b981',
      blocked: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  const formatDate = (date) => {
    if (!date) return 'No due date';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const handleExport = async (format) => {
    try {
      await exportVendorDashboardTasks(format, filters);
      toast.success(`Tasks exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export tasks');
      console.error('Export error:', error);
    }
  };

  return (
    <div className="vendor-tasks">
      {/* Header */}
      <div className="tasks-header">
        <div className="header-content">
          <h1><i className="fas fa-tasks"></i> Tasks</h1>
          <p>Manage and track your assigned tasks</p>
        </div>
        <ExportButton
          onExport={handleExport}
          formats={['excel', 'csv', 'pdf']}
          label="Export Tasks"
        />
      </div>

      {/* Analytics Cards */}
      {!loading && !error && (
        <div className="analytics-section">
          <div className="analytics-card">
            <div className="analytics-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <i className="fas fa-tasks"></i>
            </div>
            <div className="analytics-content">
              <h3>{analytics.total}</h3>
              <p>Total Tasks</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="analytics-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="analytics-content">
              <h3>{analytics.overdue}</h3>
              <p>Overdue Tasks</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="analytics-icon" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
              <i className="fas fa-calendar-day"></i>
            </div>
            <div className="analytics-content">
              <h3>{analytics.dueToday}</h3>
              <p>Due Today</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="analytics-icon" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
              <i className="fas fa-calendar-week"></i>
            </div>
            <div className="analytics-content">
              <h3>{analytics.dueThisWeek}</h3>
              <p>Due This Week</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="analytics-icon" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
              <i className="fas fa-chart-line"></i>
            </div>
            <div className="analytics-content">
              <h3>{analytics.completionRate}%</h3>
              <p>Completion Rate</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="analytics-icon" style={{ background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)' }}>
              <i className="fas fa-spinner"></i>
            </div>
            <div className="analytics-content">
              <h3>{analytics.byStatus['in_progress'] || 0}</h3>
              <p>In Progress</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="analytics-icon" style={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' }}>
              <i className="fas fa-hourglass-half"></i>
            </div>
            <div className="analytics-content">
              <h3>{analytics.byStatus['pending'] || 0}</h3>
              <p>Pending</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="analytics-icon" style={{ background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)' }}>
              <i className="fas fa-ban"></i>
            </div>
            <div className="analytics-content">
              <h3>{analytics.byStatus['blocked'] || 0}</h3>
              <p>Blocked</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search tasks..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>
        <div className="filter-group">
          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value, page: 1 })}
            className="filter-select"
          >
            <option value="">All Priority</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <button onClick={loadTasks} className="btn-refresh">
          <i className="fas fa-sync-alt"></i> Refresh
        </button>
      </div>

      {/* Tasks List */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading tasks...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>{error}</p>
          <button onClick={loadTasks} className="btn-retry">Retry</button>
        </div>
      ) : tasks.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <h3>No Tasks Found</h3>
          <p>You don't have any tasks assigned yet</p>
        </div>
      ) : (
        <>
          <div className="tasks-grid">
            {tasks.map((task) => (
              <div key={task.id} className="task-card">
                <div className="task-header">
                  <div className="task-badges">
                    <span 
                      className="priority-badge"
                      style={{ backgroundColor: getPriorityColor(task.priority) }}
                    >
                      <i className="fas fa-flag"></i> {task.priority?.toUpperCase()}
                    </span>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(task.status) }}
                    >
                      {task.status?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="task-actions">
                    <button 
                      className="btn-icon"
                      onClick={() => setSelectedTask(task)}
                      title="View Details"
                    >
                      <i className="fas fa-eye"></i>
                    </button>
                  </div>
                </div>

                <div className="task-content">
                  <h3 className="task-title">{task.title} (#{task.task_code})</h3>
                  <p className="task-description">
                    {task.description?.substring(0, 120)}
                    {task.description?.length > 120 ? '...' : ''}
                  </p>
                </div>

                <div className="task-meta">
                  <div className="meta-item">
                    <i className="fas fa-project-diagram"></i>
                    <span>{task.project?.name || 'No Project'}(#{task?.project?.code})</span>
                  </div>
                  <div className="meta-item">
                    <i className="fas fa-calendar"></i>
                    <span className={isOverdue(task.due_date) ? 'overdue' : ''}>
                      {formatDate(task.due_date)}
                      {isOverdue(task.due_date) && ' (Overdue)'}
                    </span>
                  </div>
                </div>

                <div className="task-footer">
                  <select
                    value={task.status}
                    onChange={(e) => handleStatusChange(task.id, e.target.value)}
                    className="status-select"
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Blocked">Blocked</option>
                  </select>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page === 1}
                className="btn-page"
              >
                <i className="fas fa-chevron-left"></i> Previous
              </button>
              <span className="page-info">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page === pagination.totalPages}
                className="btn-page"
              >
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="modal-overlay" onClick={() => setSelectedTask(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Task Details</h2>
              <button 
                className="btn-close"
                onClick={() => setSelectedTask(null)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-badges">
                <span 
                  className="priority-badge"
                  style={{ backgroundColor: getPriorityColor(selectedTask.priority) }}
                >
                  <i className="fas fa-flag"></i> {selectedTask.priority?.toUpperCase()}
                </span>
                <span 
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(selectedTask.status) }}
                >
                  {selectedTask.status?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <h3>{selectedTask.title}</h3>
              <div className="detail-section">
                <label>Description</label>
                <p>{selectedTask.description || 'No description provided'}</p>
              </div>
              <div className="detail-section">
                <label>Project</label>
                <p>{selectedTask.project?.name || 'No Project'}</p>
              </div>
              <div className="detail-section">
                <label>Due Date</label>
                <p className={isOverdue(selectedTask.due_date) ? 'overdue' : ''}>
                  {formatDate(selectedTask.due_date)}
                  {isOverdue(selectedTask.due_date) && ' (Overdue)'}
                </p>
              </div>
              <div className="detail-section">
                <label>Created</label>
                <p>{formatDate(selectedTask.created_at)}</p>
              </div>
              <div className="detail-section">
                <label>Last Updated</label>
                <p>{formatDate(selectedTask.updated_at)}</p>
              </div>
              <div className="detail-section">
                <label>Update Status</label>
                <select
                  value={selectedTask.status}
                  onChange={(e) => {
                    handleStatusChange(selectedTask.id, e.target.value);
                    setSelectedTask(null);
                  }}
                  className="status-select-modal"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorTasks;

